import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { assignNetSuiteAsset } from 'store/netSuiteAssetManager'
import { EDITABLE_FIELDS } from './data'
import { useFormState, useFormValidation } from './hooks'
import AssignLicense from './component'
import { Skus } from './constants'

const AssignLicenseContainer = ({
  clearSelectedLicense: clearSelectedNetSuiteAsset,
  defaultAdminEmail,
  defaultSiteData,
  license,
}) => {
  const netSuiteAsset = license
  const dispatch = useDispatch()
  const formState = useFormState(defaultSiteData)
  const validation = useFormValidation(formState, netSuiteAsset)

  const callAssignNetSuiteAsset = useCallback(
    (assetId, params) => dispatch(assignNetSuiteAsset(assetId, params)),
    [dispatch],
  )

  const siteData = useMemo(
    () => ({
      city: formState.city,
      country: formState.country,
      netSuiteCustomerId: formState.netSuiteCustomerId,
      nsCustomerId: formState.nsCustomerId,
      region: formState.region,
      siteName: formState.siteName,
    }),
    [formState],
  )

  const netSuiteAssetChecks = useMemo(() => {
    const { programCodes } = netSuiteAsset?.product || {}
    const isSelaAsset = programCodes.includes(Skus.ADLSkuCode)

    const tosRequiredSkus = [
      Skus.MSPSkuCode,
      Skus.K8SkuCode,
      Skus.K8FamilySkuCode,
      Skus.HighSchoolSkuCode,
      Skus.K12SkuCode,
      Skus.K12FamilySkuCode,
      Skus.ADLSkuCode,
    ]

    const enableTermsOfService = tosRequiredSkus.some(sku =>
      programCodes.includes(sku),
    )

    return { isSelaAsset, enableTermsOfService }
  }, [netSuiteAsset?.product?.programCodes])

  const handleChange = useCallback(
    data => {
      const fieldSetters = {
        city: formState.setCity,
        country: formState.setCountry,
        netSuiteCustomerId: formState.setNetSuiteCustomerId,
        nsCustomerId: formState.setNsCustomerId,
        region: formState.setRegion,
        siteName: formState.setSiteName,
      }

      EDITABLE_FIELDS.forEach(prop => {
        if (data.hasOwnProperty(prop) && fieldSetters[prop]) {
          fieldSetters[prop](data[prop])
        }
      })
    },
    [formState],
  )

  const handleToggle = useCallback(
    event => {
      const { name } = event.target

      switch (name) {
        case 'isAssignmentConfirmed':
          formState.setIsAssignmentConfirmed(prev => !prev)
          break
        case 'isTosConfirmed':
          formState.setIsTosConfirmed(prev => !prev)
          break
        default:
          break
      }
    },
    [formState],
  )

  const handleSubmit = useCallback(
    e => {
      e.preventDefault()
      if (validation.isReadyToSubmit) {
        assignSchoolAndAdmin()
      }
    },
    [validation.isReadyToSubmit],
  )

  const assignSchoolAndAdmin = useCallback(async () => {
    const {
      city,
      country,
      isTosConfirmed,
      nsCustomerId,
      region,
      siteName,
    } = formState

    const netSuiteAssetAssignmentRequest = {
      adminToAssign: defaultAdminEmail,
      city,
      country,
      siteName,
      ...(region && { region }),
      isTosConfirmed,
      netSuiteAssetId: netSuiteAsset.id,
      nsCustomerId: nsCustomerId === 0 ? null : nsCustomerId,
    }

    try {
      await callAssignNetSuiteAsset(
        netSuiteAsset.id,
        netSuiteAssetAssignmentRequest,
      )
      clearSelectedNetSuiteAsset()
    } catch (error) {
      console.error('Failed to assign NetSuite asset:', error)
    }
  }, [
    callAssignNetSuiteAsset,
    clearSelectedNetSuiteAsset,
    defaultAdminEmail,
    formState,
    netSuiteAsset,
  ])

  return (
    <AssignLicense
      clearSelectedNetSuiteAsset={clearSelectedNetSuiteAsset}
      enableTermsOfService={netSuiteAssetChecks.enableTermsOfService}
      handleChange={handleChange}
      handleSubmit={handleSubmit}
      handleToggle={handleToggle}
      isAssignmentConfirmed={formState.isAssignmentConfirmed}
      isReadyToSubmit={validation.isReadyToSubmit}
      isSelaAsset={netSuiteAssetChecks.isSelaAsset}
      isTosConfirmed={formState.isTosConfirmed}
      siteData={siteData}
    />
  )
}

AssignLicenseContainer.propTypes = {
  clearSelectedLicense: PropTypes.func.isRequired,
  defaultAdminEmail: PropTypes.string,
  defaultSiteData: PropTypes.shape({
    city: PropTypes.string,
    country: PropTypes.string,
    netSuiteCustomerId: PropTypes.number,
    region: PropTypes.string,
    siteName: PropTypes.string,
  }),
  license: PropTypes.object.isRequired,
}

AssignLicenseContainer.defaultProps = {
  defaultSiteData: {
    country: '',
    region: '',
    city: '',
    netSuiteCustomerId: '',
    siteName: '',
  },
}

export default AssignLicenseContainer
